/**
 * Composant Header commun pour tous les widgets
 * Inclut : logo Apidae, bouton filtres, clear filtres, nom widget, switcher
 */

import { LitElement, html, unsafeCSS, CSSResultGroup } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { WidgetLanguage } from "../types/widgets.d";
import { TranslationService } from "../services/TranslationService";
import widgetHeaderStyles from "../styles/widget-header.css?inline";

export interface WidgetHeaderConfig {
  widgetName?: string;
  logo?: string;
  logoUrl?: string;
  language?: WidgetLanguage;
  showFiltersButton?: boolean;
  showClearButton?: boolean;
  hasActiveFilters?: boolean;
  showSwitcher?: boolean;
  switcherLeftLabel?: string;
  switcherRightLabel?: string;
  switcherValue?: "left" | "right";
  filtersVisible?: boolean; // État actuel de la visibilité des filtres
  resultsCount?: number; // Nombre de résultats à afficher
  primaryColor?: string; // Couleur primaire du widget (par défaut #0094DB)
  secondaryColor?: string; // Couleur secondaire du widget (par défaut #F2F5F9)
}

@customElement("apidae-widget-header")
export class WidgetHeader extends LitElement {
  static styles: CSSResultGroup = unsafeCSS(widgetHeaderStyles);

  @property({ type: Object })
  config: WidgetHeaderConfig = {};

  @state()
  private filtersVisible: boolean = false;

  @state()
  private switcherValue: "left" | "right" = "left";

  connectedCallback() {
    super.connectedCallback();
    if (this.config.switcherValue) {
      this.switcherValue = this.config.switcherValue;
    }
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has("config")) {
      if (this.config.switcherValue !== undefined) {
        this.switcherValue = this.config.switcherValue;
      }
      // Synchroniser l'état des filtres avec la config
      if (this.config.filtersVisible !== undefined) {
        this.filtersVisible = this.config.filtersVisible;
      }
    }
  }

  private handleToggleFilters() {
    // Utiliser la valeur actuelle de la config ou basculer l'état local
    const currentVisibility =
      this.config.filtersVisible !== undefined
        ? this.config.filtersVisible
        : this.filtersVisible;
    const newVisibility = !currentVisibility;

    // Mettre à jour l'état local pour l'affichage immédiat
    this.filtersVisible = newVisibility;

    // Émettre l'événement avec la nouvelle valeur
    this.dispatchEvent(
      new CustomEvent("toggle-filters", {
        detail: { visible: newVisibility },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleClearFilters() {
    this.dispatchEvent(
      new CustomEvent("clear-filters", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleSwitcherChange() {
    this.switcherValue = this.switcherValue === "left" ? "right" : "left";
    this.dispatchEvent(
      new CustomEvent("switcher-change", {
        detail: { value: this.switcherValue },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private t(key: string): string {
    return TranslationService.translate(
      key,
      this.config.language || WidgetLanguage.FR,
    );
  }

  /**
   * Convertit une couleur hexadécimale en rgba
   */
  private hexToRgba(hex: string, alpha: number): string {
    // Retirer le # si présent
    hex = hex.replace("#", "");

    // Convertir en RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  render() {
    const {
      widgetName,
      logo,
      logoUrl,
      showFiltersButton = false,
      showClearButton = false,
      hasActiveFilters = false,
      showSwitcher = false,
      switcherLeftLabel = "List",
      switcherRightLabel = "Map",
      resultsCount,
      primaryColor = "#0094DB",
      secondaryColor = "#F2F5F9",
    } = this.config;

    // Convertir la couleur primaire en rgba avec 10% d'opacité pour le fond de la bulle
    const primaryColorRgba = this.hexToRgba(primaryColor, 0.1);

    return html`
      <header
        class="widget-header"
        style="background-color: ${secondaryColor};"
      >
        <div class="header-left">
          ${logo
            ? html`
                <a
                  class="header-logo"
                  href="${logoUrl || "#"}"
                  @click=${(e: Event) => {
                    if (!logoUrl) {
                      e.preventDefault();
                    }
                  }}
                >
                  <img src="${logo}" alt="Apidae" />
                </a>
              `
            : html` <div class="header-logo-text">Apidae</div> `}
          ${showFiltersButton
            ? html`
                <button
                  class="header-button filters-button"
                  @click=${this.handleToggleFilters}
                  aria-label="${this.t("toggleFilters")}"
                >
                  <apidae-icon
                    name=${this.filtersVisible
                      ? "bold/network_it_programming/sidebar_minimalistic_reversed"
                      : "bold/network_it_programming/sidebar_minimalistic_empty"}
                    size="20"
                    color="${primaryColor}"
                  ></apidae-icon>
                </button>
              `
            : ""}
          ${resultsCount !== undefined
            ? html`
                <div
                  class="results-badge"
                  style="background-color: ${primaryColorRgba}; gap: ${showClearButton &&
                  hasActiveFilters
                    ? "0.5rem"
                    : "0"};"
                >
                  <span class="results-count" style="color: ${primaryColor};">
                    ${resultsCount}
                    ${resultsCount > 1 ? this.t("results") : this.t("result")}
                  </span>
                  ${showClearButton && hasActiveFilters
                    ? html`
                        <button
                          class="clear-icon-button"
                          @click=${this.handleClearFilters}
                          aria-label="${this.t("clearFilters")}"
                        >
                          <apidae-icon
                            name="bold/essentional_ui/close_circle"
                            size="20"
                            color="${primaryColor}"
                          ></apidae-icon>
                        </button>
                      `
                    : ""}
                </div>
              `
            : ""}

          <span class="header-separator">|</span>

          ${widgetName ? html`<h2 class="widget-title">${widgetName}</h2>` : ""}
        </div>

        <div class="header-right">
          ${showSwitcher
            ? html`
                <div
                  class="switcher-container"
                  style="background-color: ${this.hexToRgba("#AAB4C3", 0.2)};"
                >
                  <button
                    class="switcher-button ${this.switcherValue === "left"
                      ? "active"
                      : ""}"
                    style="${this.switcherValue === "left"
                      ? `background-color: ${primaryColorRgba}; color: ${primaryColor};`
                      : `color: #AAB4C3;`}"
                    @click=${() => {
                      if (this.switcherValue !== "left") {
                        this.handleSwitcherChange();
                      }
                    }}
                  >
                    <apidae-icon
                      name="bold/settings_fine_tuning/widget"
                      size="20"
                      color="${this.switcherValue === "left"
                        ? primaryColor
                        : "#AAB4C3"}"
                    ></apidae-icon>
                    ${switcherLeftLabel}
                  </button>
                  <button
                    class="switcher-button ${this.switcherValue === "right"
                      ? "active"
                      : ""}"
                    style="${this.switcherValue === "right"
                      ? `background-color: ${primaryColorRgba}; color: ${primaryColor};`
                      : `color: #AAB4C3;`}"
                    @click=${() => {
                      if (this.switcherValue !== "right") {
                        this.handleSwitcherChange();
                      }
                    }}
                  >
                    <apidae-icon
                      name="bold/map_&_location/streets_map_point"
                      size="20"
                      color="${this.switcherValue === "right"
                        ? primaryColor
                        : "#AAB4C3"}"
                    ></apidae-icon>
                    ${switcherRightLabel}
                  </button>
                </div>
              `
            : ""}
        </div>
      </header>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-widget-header": WidgetHeader;
  }
}
